import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { UserPlus, X } from 'lucide-react';

const DISMISS_STORAGE_KEY = 'aaa_create_account_banner_dismissed';
const BANNER_HEIGHT_VAR = '--account-banner-h';

const setBannerHeightVar = (value) => {
  if (typeof document === 'undefined') return;
  document.documentElement.style.setProperty(BANNER_HEIGHT_VAR, value);
};

const isBrowser = () =>
  typeof window !== 'undefined' && typeof window.sessionStorage !== 'undefined';

const wasDismissedThisSession = () => {
  if (!isBrowser()) return false;
  try {
    return window.sessionStorage.getItem(DISMISS_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
};

const CreateAccountBanner = ({ visible, sticky = false }) => {
  const { t } = useTranslation();
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
  const [dismissed, setDismissed] = useState(wasDismissedThisSession);
  const contentRef = useRef(null);

  useEffect(() => {
    if (isAuthenticated && isBrowser()) {
      try {
        window.sessionStorage.removeItem(DISMISS_STORAGE_KEY);
      } catch {
        /* no-op */
      }
    }
  }, [isAuthenticated]);

  const handleDismiss = () => {
    setDismissed(true);
    if (isBrowser()) {
      try {
        window.sessionStorage.setItem(DISMISS_STORAGE_KEY, 'true');
      } catch {
        /* no-op */
      }
    }
  };

  const shouldShow = visible && !isAuthenticated && !dismissed;

  useEffect(() => {
    if (!shouldShow) {
      setBannerHeightVar('0px');
      return undefined;
    }

    const node = contentRef.current;
    if (!node) return undefined;

    const updateHeight = () => setBannerHeightVar(`${node.offsetHeight}px`);
    updateHeight();

    let observer;
    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(updateHeight);
      observer.observe(node);
    }

    return () => {
      observer?.disconnect();
      setBannerHeightVar('0px');
    };
  }, [shouldShow]);

  return (
    <AnimatePresence>
      {shouldShow && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.3, ease: 'easeInOut' }}
          className={`overflow-hidden ${
            sticky ? 'fixed top-0 left-0 right-0 z-[60]' : ''
          }`}
          role="region"
          aria-label={t('create_account_banner_label', 'Create a free account')}
        >
          <div ref={contentRef} className="bg-primary text-primary-foreground">
            <div className="max-w-5xl mx-auto px-4 py-2.5">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2.5 min-w-0 flex-1">
                  <UserPlus className="w-4 h-4 flex-shrink-0" aria-hidden="true" />
                  <p className="text-xs sm:text-sm font-medium min-w-0">
                    <span className="sm:hidden">
                      {t(
                        'create_account_banner_body_short',
                        'Create a free account today!'
                      )}
                    </span>
                    <span className="hidden sm:inline">
                      {t(
                        'create_account_banner_body',
                        'Create a free account today — post about pets, message other users, and bookmark your favorite places.'
                      )}
                    </span>
                  </p>
                </div>

                <div className="flex items-center gap-1.5 flex-shrink-0">
                  <Link
                    to="/sign-up"
                    className="px-3 py-1.5 bg-white/20 hover:bg-white/30 rounded-lg
                               text-xs sm:text-sm font-semibold transition-colors
                               whitespace-nowrap"
                  >
                    {t('create_account_banner_cta', 'Sign up')}
                  </Link>
                  <button
                    type="button"
                    onClick={handleDismiss}
                    aria-label={t('dismiss', 'Dismiss')}
                    className="p-1.5 rounded-lg hover:bg-white/20 transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CreateAccountBanner;
